"use client";

import { Alert, Box, Container, Group, List, Paper, Text, Title } from "@mantine/core";
import { IconArrowNarrowRight } from "@tabler/icons-react";

import { FrameworkBreadcrumb } from "@/app/(marketing)/decision-making-frameworks/components/FrameworkBreadcrumb";
import {
  CONSEQUENTIAL_THINKING_FRAMEWORK_NAME,
  DECISION_MAKING_FRAMEWORKS,
} from "@/app/(marketing)/decision-making-frameworks/constants";

export default function ConsequentialThinkingPage() {
  const framework = DECISION_MAKING_FRAMEWORKS[CONSEQUENTIAL_THINKING_FRAMEWORK_NAME];

  return (
    <Container size="md" py="xl">
      <FrameworkBreadcrumb framework={framework} />

      <Title order={1} mb="md">
        {framework.name}
      </Title>
      <Text size="lg" c="dimmed" mb="xl">
        {framework.description}
      </Text>

      <Alert variant="light" color="blue" mb="xl">
        <Text>
          <Text span fw={600}>
            Some decisions seem like wins at first, but turn out to be losses over time.
          </Text>{" "}
          What seems like an investment now turns out to be a liability later. Consequential
          thinking is a tool that will help you examine the long-term effects of your decisions.
        </Text>
        <Text mt="md">
          Most people stop at first-order thinking, but second and third-order thinking is necessary
          to make decisions that will stand the test of time. We need to make sure we're okay with
          the long-term consequences of our decisions today.
        </Text>
      </Alert>

      <Title order={2} mb="md">
        How to use consequential thinking
      </Title>
      <Text mb="md">
        Using consequential thinking can be a purely mental exercise or you can write it down on
        paper:
      </Text>
      <List spacing="sm" mb="xl">
        <List.Item>
          Consider a decision you have to make. Start by looking at the most immediate effects of
          making this decision – the first order.
        </List.Item>
        <List.Item>
          For each of the effects ask yourself: "And then what?" That's how you examine the second
          order of this decision's consequences.
        </List.Item>
        <List.Item>
          Repeat this for third-order consequences by asking "And then what?" again.
        </List.Item>
        <List.Item>
          Alternatively, think about the decision in different timelines:
          <List withPadding mt="xs">
            <List.Item>What will be the consequences in 10 minutes?</List.Item>
            <List.Item>What will be the consequences in 10 months?</List.Item>
            <List.Item>What will be the consequences in 10 years?</List.Item>
          </List>
        </List.Item>
        <List.Item>
          You can apply consequential thinking to both big decisions (e.g., buying a house) and
          small, seemingly mundane decisions (e.g., eating a cake).
        </List.Item>
      </List>

      <Title order={2} mb="md">
        Consequential thinking in practice
      </Title>
      <Text mb="md">
        Let's explore what consequential thinking looks like in action. Consider a leadership
        decision about implementing a new project management system.
      </Text>

      <Text fw={500} mb="xs">
        First-order effects:
      </Text>
      <List withPadding mb="md">
        <List.Item>Standardized workflow across teams</List.Item>
        <List.Item>Initial investment in software and training</List.Item>
        <List.Item>Temporary productivity slowdown during transition</List.Item>
      </List>

      <Text fw={500} mb="xs">
        Now let's examine the higher-order consequences:
      </Text>

      <Paper p="lg" withBorder radius="md" mb="md">
        <Box mb="md">
          <Text fw={600}>First order: Standardized workflow across teams</Text>
          <Group ml="md" mt="xs">
            <IconArrowNarrowRight size={16} />
            <Text fw={500}>Second order: Improved cross-team collaboration</Text>
          </Group>
          <Group ml={50} mt="xs">
            <IconArrowNarrowRight size={16} />
            <Text>Third order: Faster delivery of interdependent projects</Text>
          </Group>
        </Box>

        <Box mb="md">
          <Text fw={600}>First order: Initial investment in software and training</Text>
          <Group ml="md" mt="xs">
            <IconArrowNarrowRight size={16} />
            <Text fw={500}>Second order: Budget constraints for other initiatives</Text>
          </Group>
          <Group ml={50} mt="xs">
            <IconArrowNarrowRight size={16} />
            <Text>Third order: Delayed innovation in other areas of the business</Text>
          </Group>
        </Box>

        <Box>
          <Text fw={600}>First order: Temporary productivity slowdown</Text>
          <Group ml="md" mt="xs">
            <IconArrowNarrowRight size={16} />
            <Text fw={500}>Second order: Missed quarterly targets</Text>
          </Group>
          <Group ml={50} mt="xs">
            <IconArrowNarrowRight size={16} />
            <Text>Third order: Reduced team bonuses and potential talent attrition</Text>
          </Group>
        </Box>
      </Paper>

      <Text mb="xl">
        This example shows how a seemingly straightforward decision to improve processes can have
        cascading effects across the organization. By thinking through these consequences in
        advance, a leader can develop mitigation strategies, set appropriate expectations with
        stakeholders, and potentially phase the implementation to reduce negative impacts.
      </Text>
    </Container>
  );
}
